import { next, once } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { APIData, DSSetup, Service, ValidationError } from 'webapp-types';
import { BlockVisualization, ModelName } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { copyObject, equal } from 'webapp/utils/common';

import type { SampleRecord } from 'webapp/models';
import BoardState from 'webapp/models-custom/board-state';
import type { ValueSelected } from 'webapp/models-custom/custom-metric-setup-form/item/field';
import type CustomMetricSetupFormItemField from 'webapp/models-custom/custom-metric-setup-form/item/field';
import type CustomQuery from 'webapp/models-custom/custom-query';
import DatawallGrid from 'webapp/models-custom/datawall-grid';
import DateRange from 'webapp/models-custom/date-range';
import type InfolayerItemGeneral from 'webapp/models-custom/infolayer-item/general';
import Serviced from 'webapp/models-custom/serviced';
import StatusObject from 'webapp/models-custom/status-object';

import type { RSjqXHR, RSPromise } from 'webapp/services/request';

type BuilderResponse =
	| APIData.CustomQuery.BuilderPrebuiltResponse
	| APIData.CustomQuery.BuilderSheetResponse
	| APIData.CustomQuery.BuilderAPIMetricBuilderResponse;

// a preview error: the request's errors, or the statuses as html (a jQuery element on prebuilt forms)
export interface PreviewErrorData<Html = string | JQuery<HTMLElement>> {
	html?: Html;
	errors?: APIData.CustomQuery.BuilderMessage[];
}

export default abstract class CustomMetricSetupFormBase extends Serviced {
	@service('account') declare accountService: Service.Account;
	@service('store') declare storeService: Service.Store;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('request') declare requestService: Service.Request;
	@service('popup') declare popupService: Service.Popup;
	@service('logger') declare loggerService: Service.Logger;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;

	@tracked query: CustomQuery;

	@tracked originalSettings: {
		builder: APIData.CustomQuery.BuilderData;
		settings: APIData.CustomQuery.SettingsData;
	} | null = null;

	@tracked transformsData: APIData.CustomQuery.TransformationsData | null = null;

	@tracked configStatusObject: APIData.CustomQuery.BuilderConfigStatusObject | null = null;

	@tracked trackingOrigin = '';

	@tracked openMetricDefinitionPopupOnInit = false;

	allowTransformations = true;

	constructor(query: CustomQuery) {
		super();
		this.query = query;

		next(this, this.createBlockAndBoardState);
	}

	abstract get isWizardFlowSupported(): boolean;

	get isNew(): boolean {
		return this.query.isNew;
	}

	get builderUrl(): string {
		return this.query.builderUrl;
	}

	// ------- Initializing --------

	isLoading = false; // just for compatibility in templates

	@tracked isLoadingBuilder = false;

	initBuilder(): Promise<unknown> {
		if (!this.query.sourceId) {
			return Promise.resolve();
		}

		this.isLoadingBuilder = true;

		return this.fetchBuilder()
			.then((reply) => {
				try {
					this.initData(reply);
					this.shouldShowConfigStatusObjectPopup();
				} finally {
					this.isLoadingBuilder = false;
				}
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	fetchBuilder(forceRefresh = false): Promise<BuilderResponse> {
		let url = this.builderUrl;

		const cloneFrom = this.query.cloneFrom;
		if (cloneFrom && !this.query.id) {
			url = `/customqueries/${cloneFrom}/builder/${this.query.sourceId}`;
		}

		if (forceRefresh) {
			url += '?forceRefresh=true';
		}

		// const data = window.dbx_mock['GET /d/customqueries/<qid>/builder/<sid> - sheets 1'];
		// const data = window.dbx_mock['GET /d/customqueries/<qid>/builder/<sid> - sheets 2'];
		// const data = window.dbx_mock['GET /d/customqueries/<qid>/builder/<sid> - datasets hbsp'];
		// data.settings.metric = '\'Simple\'!D:D';
		// data.settings.metric = 'B:B'; // Missing cells sheet
		// return Promise.resolve(data);
		return this.requestService.api('GET', url, null, {
			timeout: 5 * 60 * 1000,
		});
	}

	initData(data: BuilderResponse) {
		this.originalSettings = {
			builder: data.builder,
			settings: data.settings,
		};
		this.configStatusObject =
			'statusObject' in data && data.statusObject ? data.statusObject : null;
	}

	shouldShowConfigStatusObjectPopup(): void {
		if (!this.configStatusObject) {
			return;
		}
		const { title, description } = this.configStatusObject;
		const message = description ?? title ?? 'Metric configuration error';
		this.onError({ errors: [{ message }] }, 'Metric Builder error');
	}

	onFormLoaded(_node: HTMLElement): void {
		// maybe extended
	}

	onFormUnload(): void {
		// maybe extended
		this.cancelQueryPreview();

		if (this.boardState) {
			// handle cases where its not init-ed yet
			const fakeBlock = this.previewBlock;
			if (fakeBlock) {
				void fakeBlock.delete(true);
			}
		}
	}

	// ------ current form status & handlers ---------

	@tracked externalFieldUpdate: (fieldInfo: SomeDataObject) => void = () => {
		/* set from outside */
	};

	@tracked previewTakingTooLongInfolayer: InfolayerItemGeneral | null = null; // after 20s allow saving even if preview has not finished yet

	// can be specifically set so we can retrieve settings in another way than building all form items
	// e.g. when already have them on the custom query (wizard-create-block flwo)
	prepareSettingsFn: (() => APIData.CustomQuery.SettingsData) | null = null;

	abstract get isPreviewButtonDisabled(): boolean;

	abstract get isFormReady(): boolean;

	abstract prepareSettings(): APIData.CustomQuery.SettingsData;

	abstract checkAreItemsWithDefinesRawIdChanged(): boolean;

	abstract updateExternalField(fieldId: string, fieldValue: ValueSelected): void;

	abstract handleItemAction(item: string, action: string): void;

	onQueryPreviewFinished?(reply: APIData.CustomQuery.PreviewResponse): void;

	get isDirty(): boolean {
		let isDirty = false;
		if (this.lastQueriedPreviewPayload) {
			isDirty = !equal(
				(this.lastQueriedPreviewPayload as APIData.CustomQuery.PreviewRequest).settings,
				this.prepareSettings(),
			);
		}
		return isDirty;
	}

	get isPreviewTakingTooLong() {
		return !!this.previewTakingTooLongInfolayer;
	}

	get isSaveButtonDisabled(): boolean {
		return this.isDirty || (this.isPreviewButtonLoading && !this.isPreviewTakingTooLong);
	}

	get isAttributed(): boolean {
		return !!(this.previewDataForBottomTable?.headers || []).find((item) => item.isAttribute);
	}

	get previewButtonText(): string {
		return 'Preview data';
	}

	get isPreviewButtonLoading(): boolean {
		return this.isLoadingPreview;
	}

	updateTransforms(
		property: APIData.CustomQuery.TransformationsDimensionDataProperty,
		data:
			| APIData.CustomQuery.TransformationsDimensionDataAttributesMap
			| APIData.CustomQuery.TransformationsDimensionDataAttributesSkip,
		skipPreview = false,
	): void {
		const dimension = this.selectedPreviewDimension,
			transformsData = this.transformsData || {},
			dimensionData = transformsData[dimension] || {
				attributesMap: {},
				attributesSkip: [],
			};
		if (!equal(dimensionData?.[property], data)) {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			dimensionData[property] = (data as any) || null;
			transformsData[dimension] = dimensionData;
			this.transformsData = transformsData;
			if (!skipPreview) {
				void this.queryPreview();
			}
		}
	}

	onContentChange(): void {
		// maybe extended
	}

	// ------- Fake block stuff --------

	@tracked boardState!: BoardState;

	get previewBlock() {
		return this.boardState.selectedBlock!;
	}

	get previewBlockState() {
		return this.boardState.getBlockState(this.previewBlock)!;
	}

	get metricLimitations() {
		return this.previewBlockState.limitationsCM!; // through block-state, to catch changes
	}

	private createBlockAndBoardState() {
		const params: SomeDataObject = {
			id: this.mmCustomQueriesService.generateTemporaryFakeId(),
			name: this.query.queryName,
			order: 0,
			width: DatawallGrid.minimumSize * 4, // for blockSizeCoeff (fontsizes)
			height: DatawallGrid.minimumSize * 4, // for blockSizeCoeff (fontsizes)
			visualization: BlockVisualization.BAR_CHART,
		};

		const block = this.mmBlocksService.createNewModel(params);
		const boardState = BoardState.createForSingleBlock(block);
		boardState.isViewOnly = false;
		boardState.explicitlyDisableAllPreviews = false;
		boardState.onStateChange = (payload) => {
			const changes = payload.blockPreviews?.[0]?.changes;
			if (!changes) {
				return;
			}
			if (changes.dateRange) {
				void this.queryPreview();
			} else if (changes.settings?.visualization) {
				// bcos change in visualization without change in daterange keeps the data
				// we apply the visualization change directly on the block
				// where it will get picked up by wrapper:checkToRecreateVisualization
				this.previewBlock.visualization = changes.settings.visualization;
			}
		};
		this.boardState = boardState;

		const blockSetup = block.blockSetup;
		blockSetup.isAutosaveOn = false;
	}

	// ------- Dimension selection --------

	@tracked availableDimensions: DSSetup.DimensionItem[] = [];

	@tracked selectedPreviewDimension = '';

	get selectedPreviewDimensionName(): string {
		return (
			this.availableDimensions.find((d) => d.key === this.selectedPreviewDimension)?.value ||
			''
		);
	}

	protected checkToResetSelectedPreviewDimension(
		_dimensionsItem: CustomMetricSetupFormItemField,
	): void {
		// const possibleValues = (dimensionsItem.value as string[]) || [],
		// 	currentSelected = this.selectedPreviewDimension;
		// if (currentSelected && !possibleValues.includes(currentSelected)) {
		// 	console.log('resetting', { currentSelected, possibleValues });
		// 	this.selectedPreviewDimension = '';
		// }
	}

	// -------- form data & preview ---------

	@tracked lastQueriedPreviewPayload:
		APIData.CustomQuery.PreviewRequest | Record<string, never> | null = null; // Record<string, never> === {}
	@tracked isLoadingPreview = false;
	@tracked previewData: APIData.CustomQuery.PreviewResponse['previewData'] | null = null;
	@tracked wasLastPreviewTimeout = false;

	private previewPromise: Promise<unknown> | null = null;

	get previewDataForBottomTable(): APIData.CustomQuery.PreviewDataForBottomTable | null {
		return this.previewData;
	}

	scheduleQueryPreview(): void {
		if (this.isValid) {
			next(() => {
				once(this, this.queryPreview);
			});
		}
	}

	// for refetch status object action
	triggerRefetchPreview() {
		this.lastQueriedPreviewPayload = {};

		return this.queryPreview();
	}

	triggerGetPreview(): void {
		void this.queryPreview();
	}

	getPreviewPayload(): APIData.CustomQuery.PreviewRequest {
		// each form sends its own kind of settings
		const data = {
			settings: this.prepareSettings(),
			transformations: this.transformsData,
			block: this.previewBlock.toSerializedJSON(true),
			fromDesigner: true, // connectors needs this to format response bcos same endpoint is called from BE
		} as APIData.CustomQuery.PreviewRequest;

		if (this.selectedPreviewDimension) {
			data.dimension = this.selectedPreviewDimension;
		}

		return data;
	}

	private isSameAsLastQueried(data: APIData.CustomQuery.PreviewRequest): boolean {
		const compare1 = Object.assign(
				{},
				this.lastQueriedPreviewPayload,
			) as APIData.CustomQuery.PreviewRequest,
			compare2 = Object.assign({}, data);

		// not important field
		delete compare1.fromDesigner;
		delete compare2.fromDesigner;

		// check for transformations prop: initially queried null is the same as empty keys (returned from BE)
		if (
			!compare1.transformations &&
			compare2.transformations &&
			!Object.keys(compare2.transformations).length
		) {
			// same transformations, ignore
			compare1.transformations = null;
			compare2.transformations = null;
		} else {
			if (compare2.dateRange) {
				// dateranges can be equal with different key order
				if (compare1.dateRange) {
					const dateRange = DateRange.createFromFormula(compare1.dateRange);
					if (!dateRange.isEqual(compare2.dateRange)) {
						return false;
					}
				} else {
					return false;
				}
			}
			// same dateranges, ignore
			delete compare1.dateRange;
			delete compare2.dateRange;
		}

		return equal(compare1, compare2);
	}

	get isDefaultDateRange() {
		return this.previewBlock?.blockSetup?.isDefaultDateRange();
	}

	get isDefaultStartingDateRange() {
		return this.isDefaultDateRange && !this.previewData;
	}

	queryPreview(): Promise<unknown> {
		if (!this.isValid) {
			return Promise.resolve();
		}

		if (!this.builderUrl) {
			return Promise.resolve();
		}

		const data = this.getPreviewPayload();

		if (!Object.keys(data.settings).length) {
			return Promise.resolve();
		}

		// send dateRange only after first query (when we know what is available)
		// or, if the previous request timeouted
		// or, if user has changed the default
		if (this.previewData || this.wasLastPreviewTimeout || !this.isDefaultDateRange) {
			const dateRange = this.metricLimitations.dateRange;
			if (dateRange && !dateRange.isEmpty) {
				data.dateRange = dateRange?.formula;
			}
		}

		// only request if not the same or not requested yet
		// checking previewXHR is added to not cancel the initial request (last queried data is set on init too)
		if (this.previewPromise && this.isSameAsLastQueried(data)) {
			return Promise.resolve();
		}

		const previewBlockId = this.previewBlock.numericId!;

		// empty sample to show loader
		(
			this.storeService.peekRecord(ModelName.SAMPLE, previewBlockId) as SampleRecord
		)?.unloadRecord();

		// for the first ever request, create one fake DS bcos of the loader logic
		if (!this.mmDataSelectsService.getRecordsForBlock(previewBlockId).length) {
			this.storeService.pushPayload({
				dataSelect: {
					id: this.mmCustomQueriesService.generateTemporaryFakeId(),
					blockId: previewBlockId,
					dateRange: this.metricLimitations.dateRange?.formula,
				},
			});
		}

		// if request already in progress, cancel it
		this.cancelQueryPreview();

		this.errors = [];

		this.isLoadingPreview = true;

		// set lastQueriedPreviewPayload here and also on response
		// to avoid doing double requests at the start before first response comes in
		this.lastQueriedPreviewPayload = data;

		const promise = this.requestService.api<APIData.CustomQuery.PreviewResponse>(
			'POST',
			`${this.builderUrl}/preview`,
			data,
			{
				timeout: 5 * 60 * 1000,
			},
		);
		this.previewPromise = promise;

		const eventBody: APIData.Tracking.Data = {
			origin: this.trackingOrigin,
			datasource: this.query.sourceInfo?.key,
		};

		if (this.query.sourceObj?.isTypeOfDataset) {
			eventBody.type = this.query.sourceObj?.selectedSourceOption?.slice(0, -1);
		}

		this.analyticsService.event(TrackingEvent.QUERY_PREVIEWED, eventBody);

		// set timer for isPreviewTakingTooLong to enable save button anyway
		const tooLongTimeoutMs = 20000; // 20s
		setTimeout(() => {
			if (this.isLoadingPreview && this.previewPromise === promise) {
				const tooLongInfolayer = this.infolayerService.loading(
					'The preview is taking longer than usual. You can save the metric anyways.',
				);
				tooLongInfolayer.expire(30000);
				this.previewTakingTooLongInfolayer = tooLongInfolayer;
			}
		}, tooLongTimeoutMs);

		return promise
			.then(
				(reply) => {
					// TEST DATA STARTS HERE

					// gsheets with sample, table
					// reply = window.dbx_mock['POST /d/customqueries/<qid>/builder/<sid>/preview - sheets 1'];
					// gsheets with sample, chart
					// reply = window.dbx_mock['POST /d/customqueries/<qid>/builder/<sid>/preview - sheets 2'];{
					// other QBs
					// NextQuarter has daily gran disabled. returns this week.
					// reply = window.dbx_mock['POST /d/customqueries/<qid>/builder/<sid>/preview - normal 1'];

					// bcos of updateFrom will work correctly only on series visualizations
					// reply =
					// 	window.dbx_mock[
					// 		'POST /d/customqueries/<qid>/builder/<sid>/preview - normal 1'
					// 	];
					// if (data.dateRange) {
					// 	reply.previewData.dateRange = data.dateRange;
					// }

					// if (reply.dataSelects) {
					// 	reply.dataSelects.forEach((dataSelect) => {
					// 		dataSelect.blockId = previewBlockId;
					// 		if (data.dateRange) {
					// 			dataSelect.dateRange = data.dateRange;
					// 		}
					// 	});
					// }
					// if (reply.previewData.sample) {
					// 	reply.previewData.sample.id = previewBlockId;
					// 	if (data.dateRange) {
					// 		reply.previewData.sample.sampledata.dateRange = data.dateRange;
					// 	}
					// }
					// TEST DATA ENDS HERE

					if (reply.limitations) {
						this.metricLimitations.rawLimitations = reply.limitations.ranges;
					} else {
						this.loggerService.logError(
							'[CDR][qb] Preview did not return limitations',
							{ url: this.builderUrl },
						);
					}

					const dateRange = reply.previewData.dateRange;
					data.dateRange = dateRange;

					if (reply.dimension) {
						data.dimension = reply.dimension;
					}

					this.lastQueriedPreviewPayload = data; // set immediately so DR change on block state triggering preview skips request

					if (dateRange) {
						this.metricLimitations.updateDateRangeFromFormula(dateRange);
						this.previewBlockState.limitationsCM!.updateDateRangeFromFormula(dateRange);
						this.previewBlock.directPropUpdate({
							dateRange: copyObject(dateRange),
						});
					} else {
						this.loggerService.logError('[CDR][qb] Preview did not return dateRange', {
							url: this.builderUrl,
						});
					}

					// cleanup previous dataSelects
					this.mmDataSelectsService
						.getRecordsForBlock(previewBlockId)
						.forEach((record) => record.unloadRecord());

					// and add new ones
					if (reply.dataSelects) {
						this.storeService.pushPayload({
							dataSelects: reply.dataSelects,
						});
					}

					// and add data
					if (reply.previewData.sample) {
						this.storeService.pushPayload({ sample: reply.previewData.sample });
					}

					this.isLoadingPreview = false;
					this.previewTakingTooLongInfolayer?.close();
					this.previewData = reply.previewData;
					this.transformsData = reply.transformations;

					this.selectedPreviewDimension = reply.dimension || '';
					this.availableDimensions = reply.dimensions || [];
					// this.availableDimensions = [
					// 	{ key: 'dim1key', value: 'Dim1value' },
					// 	{ key: 'dim2key', value: 'Dim2value' },
					// ];

					//reply.statusObjects = [{"title":"Unauthorized","description":"Request had invalid authentication credentials.","trigger":"unauthorised","userAction":"refetch","type":"request"},{"type":"sync","level":"info","title":"Your data is still fetching"},{"type":"connection","level":"error","title":"Subscription expired","description":"We can no longer sync your AccuRanker data due expired AccuRanker subscription. You'll need to reconnect your AccuRanker account when you'll have an active subscription.","userAction":"reconnect","extra":{"sourceId":123,"sourceTypeKey":"AccuRanker","sourceTitle":"Zomg AccuRanker account name"}}];

					const statusObjects = StatusObject.createFromDataArray(reply.statusObjects);
					statusObjects.forEach((so: StatusObject) => {
						so.queryId = this.query.numericId!;
					});

					this.handlePreviewErrorFromStatusObjects(statusObjects);

					this.onQueryPreviewFinished?.(reply);
				},
				(errorData) => {
					if (errorData.isBlockedOnDemo) {
						this.isLoadingPreview = false;
						this.previewTakingTooLongInfolayer?.close();
						this.accountService.handleDemo();
						return;
					}

					const statusText = errorData.obj?.statusText;

					// ignore only aborted
					if (statusText === 'abort') {
						return;
					}

					this.wasLastPreviewTimeout = statusText === 'timeout' || !errorData.code;

					// test data
					// errorData.errors = [{title: 'zomg error', description: 'zomg description', link: 'https://databox.com'}];

					this.handlePreviewError(errorData);
					this.previewData = null;
					this.isLoadingPreview = false;
					this.previewTakingTooLongInfolayer?.close();
					this.lastQueriedPreviewPayload = {};
				},
			)
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	private cancelQueryPreview() {
		const previewLoadingXHR = (this.previewPromise as RSPromise<unknown>)?.jqPromise as
			RSjqXHR | undefined;
		if (previewLoadingXHR && previewLoadingXHR.readyState !== 4) {
			previewLoadingXHR.abort();
		}
	}

	// -------- validation & error handling ---------

	@tracked errors: ValidationError[] = [];

	@tracked onError: (errorData: PreviewErrorData, msg: string) => void = () => {
		/* set from outside */
	};

	abstract get isValidAndValidated(): boolean;

	abstract get isValidatingWithRequest(): boolean;

	abstract handlePreviewErrorFromStatusObjects(statusObjects: StatusObject[]): void;

	abstract handlePreviewError(errorData: PreviewErrorData): void;

	abstract validate(): Promise<boolean>;

	get isValid(): boolean {
		return this.isValidAndValidated;
	}

	checkIfTransformsValid(): void {
		// if previewData does not have attributes column, remove transforms for attributes
		const previewDataHeaders = this.previewDataForBottomTable?.headers || [];
		if (previewDataHeaders && !previewDataHeaders.find((item) => item.isAttribute)) {
			this.transformsData = {};
		}
	}

	getFormattedError(
		errorData: APIData.CustomQuery.BuilderMessage[],
		skipLineBreaks = false,
	): string {
		// used for preview errors on gsheets & validation errors on all fields (currently - gsheets)
		// custom format just for qb (title, description, link), or default format (message)
		const errorTexts: string[] = [];

		errorData.forEach((err) => {
			let errHtml = '<strong>' + (err.title || err.message) + '</strong>';

			if (err.description) {
				if (!skipLineBreaks) {
					errHtml += '<br>';
				}
				errHtml += ' ' + err.description;
			}

			if (err.link) {
				if (!skipLineBreaks) {
					errHtml += '<br>';
				}
				errHtml += ' <a href="' + err.link + '" target="_blank">Read more</a>';
			}

			errorTexts.push(errHtml);
		});

		return errorTexts.join('<br>');
	}

	checkResetErrors(): void {
		if (this.isValidatingWithRequest && this.errors.length) {
			this.errors = [];
		}
	}
}
